import { cn } from '@/lib/utils'

export type BadgeTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'primary'

const TONES: Record<BadgeTone, string> = {
  success: 'bg-[var(--color-tint-green)] text-[var(--color-success)]',
  warning: 'bg-[var(--color-tint-orange)] text-[var(--color-warning)]',
  danger: 'bg-[var(--color-tint-red)] text-[var(--color-danger)]',
  info: 'bg-[var(--color-tint-blue)] text-[var(--color-info)]',
  primary: 'bg-[var(--color-primary-soft)] text-[var(--color-primary)]',
  neutral: 'bg-[var(--color-page)] text-[var(--color-body)]',
}

/**
 * Maps the workflow and domain statuses used across the app to a tone, so "APPROVED"
 * looks the same whether it is a leave request, an expense claim or a payroll run.
 */
const STATUS_TONES: Record<string, BadgeTone> = {
  ACTIVE: 'success', APPROVED: 'success', PAID: 'success', COMPLETED: 'success', PRESENT: 'success', CONFIRMED: 'success',
  PENDING: 'warning', PROCESSING: 'warning', DRAFT: 'warning', ON_LEAVE: 'warning', PROBATION: 'warning', RETURNED: 'warning',
  REJECTED: 'danger', ABSENT: 'danger', FAILED: 'danger', TERMINATED: 'danger', LOCKED: 'danger', STALLED: 'danger',
  CANCELLED: 'neutral', INACTIVE: 'neutral', DISABLED: 'neutral', ARCHIVED: 'neutral',
  INVITED: 'info', SCHEDULED: 'info', IN_PROGRESS: 'info', HALF_DAY: 'primary',
}

export interface StatusBadgeProps {
  status: string
  /** Overrides the status→tone lookup. */
  tone?: BadgeTone
  label?: string
  className?: string
}

export function StatusBadge({ status, tone, label, className }: StatusBadgeProps) {
  const resolved = tone ?? STATUS_TONES[status?.toUpperCase()] ?? 'neutral'
  const text = label ?? humanise(status)

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
        TONES[resolved],
        className,
      )}
    >
      {text}
    </span>
  )
}

/** `IN_PROGRESS` -> `In progress` */
function humanise(value: string): string {
  if (!value) return ''
  const spaced = value.replace(/_/g, ' ').toLowerCase()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}
